import React from "react";
import { SegmentedControl } from "@/components/ui/segmented-control";
import type { SegmentedControlOption } from "@/components/ui/segmented-control";
import { zoomToGranularity } from "@/stores/documents/repository";
import type { TimelineGranularity } from "@/stores/documents/repository";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Representative zoom level for each granularity. They mirror the bands in
 * `zoomToGranularity` (year <25, month <50, week <75, day >=75), each sitting
 * mid-band and below the 90 max-zoom threshold so picking one always lands in
 * cluster mode with that bucket size. Per-document detail lives inside Days
 * (small day buckets render one card per document), so there is no separate
 * documents segment.
 */
const GRANULARITY_ZOOM: Record<TimelineGranularity, number> = {
  day: 82,
  week: 62,
  month: 37,
  year: 12,
};

interface TimelineGranularityControlProps {
  /** Current zoom level (0-100) — the active option is derived from it. */
  zoomLevel: number;
  /** Set the zoom level to the picked option's representative value. */
  onZoomChange: (level: number) => void;
}

/**
 * TimelineGranularityControl — replaces the continuous zoom slider with an
 * explicit [Days | Weeks | Months | Years] segmented control. Selecting an
 * option jumps the zoom to that band; the active segment tracks whatever band
 * the current zoom falls in (including wheel-zoom).
 */
export const TimelineGranularityControl = React.memo(function TimelineGranularityControl({
  zoomLevel,
  onZoomChange,
}: TimelineGranularityControlProps) {
  const { t } = useLanguage();
  const active: TimelineGranularity = zoomToGranularity(zoomLevel);
  const options: SegmentedControlOption<TimelineGranularity>[] = [
    { value: "day", label: t.timeline.zoomGranularity.days },
    { value: "week", label: t.timeline.zoomGranularity.weeks },
    { value: "month", label: t.timeline.zoomGranularity.months },
    { value: "year", label: t.timeline.zoomGranularity.years },
  ];

  return (
    <SegmentedControl
      value={active}
      onValueChange={(option) => onZoomChange(GRANULARITY_ZOOM[option])}
      options={options}
    />
  );
});
